//@flow
import React, {useCallback, useMemo, useState} from 'react';
import {useUserData} from "../../../hooks/user/useUserData";
import {useTranslation} from "react-i18next";
import useUserAction from "../../../hooks/user/useUserAction";
import Title from "../../title/title";
import Divider from "../../divider/divider";
import RangeSelect from "../../rangeSelect/rangeSelect";
import moment from "moment";
import {PAGE_REPORTS} from "../../../locales/pages/namespaces";
import Util from "../../util/util";
import Table from "../../table/table";
import './report7.css'
import {withPermissionHOC} from "../../../modules/permission/components/permissionHOC/permissionHOC";
import {API, Permissions} from "../../../helpers/constants";
import TextFilter from "../../table/partials/filters/textFilter/textFilter";
import NumberRangeFilter from "../../table/partials/filters/numberRangeFilter/numberRangeFilter";
import {formatDate, formatTypes} from "../../../helpers/date";
import useSalesPersons from "../../../hooks/salesPersons/useSalesPersons";
import SelectSalesPerson from "../../selectSalesPerson/selectSalesPerson";
import Button from "../../button/button";
import {AuthenticatedLink} from "../../authenticatedLink/authenticatedLink";
import Icon from "../../icon/icon";
import Images from "../../../assets/images";

const NumberCell = ({value}) => (
    <div style={{textAlign: "right"}}>
        {Number(value).toFixed(2)}
    </div>
)

const Report7 = () => {
    const {t} = useTranslation(PAGE_REPORTS)
    const {data} = useUserData()
    const {loading, report7IndexTable} = useUserData()
    const {getReport7Info} = useUserAction()
    const {salesPersons} = useSalesPersons()
    const [selectedSalesPerson, setSelectedSalesPerson] = useState(data.id)
    const [range, setRange] = useState([
        moment().subtract(3, 'months').toDate(), new Date()
    ])
    const onSubmit = (values) => {
        const {range} = values
        setRange(range)
    }
    const salesPersonsIds = selectedSalesPerson === 'all' ? salesPersons.map(salesPerson => salesPerson.id).toString() : selectedSalesPerson
    const dataManager = useCallback((pageIndex, pageSize, sortBy) => {
        getReport7Info(pageIndex, pageSize, range, salesPersonsIds, sortBy)
    }, [getReport7Info, range, salesPersonsIds])

    const kpis = useMemo(() => (
        report7IndexTable
            .items
            .reduce((p, n) => {
                p.netTotal += n.netTotal || 0
                p.vatTotal += n.vatTotal || 0
                p.salesNetTotal += n.salesNetTotal || 0
                p.salesVatTotal += n.salesVatTotal || 0
                return p
            }, {netTotal: 0, vatTotal: 0, salesNetTotal: 0, salesVatTotal: 0})
    ), [report7IndexTable])

    return (
        <div className={'report1-page contained-page bg-container'}>
            <Util>
                <Title variant={"h3"}>
                    {t('SALES PERSON ORDERS - TOTAL AMOUNTS COMPARISON')}
                </Title>
            </Util>
            <Divider/>
            <RangeSelect
                initialRange={range}
                onSubmit={onSubmit}
            />
            <div className={'report1-page-toolbar'}>
                <SelectSalesPerson
                    value={selectedSalesPerson}
                    onChange={(evt) => setSelectedSalesPerson(evt.target.value)}
                    salesPersons={salesPersons}
                />
                <Button.Group size={'xs'}>
                    <Button
                        url={`${API}/en/b2b/report/salesperson/general/orders?fromDate=${formatDate(range[0], formatTypes.DATE_ONLY_FILTER)}&toDate=${formatDate(range[1], formatTypes.DATE_ONLY_FILTER)}&salesPersonsIds=${salesPersonsIds}&isExport=1`}
                        size={'sm'}
                        filename={'reportExport'}
                        variant={'link'}
                        component={AuthenticatedLink}
                    >
                        <Util>
                            <Icon icon={Images.icons.paperclip}/>
                            {t('Export Report')}
                        </Util>
                    </Button>
                </Button.Group>
            </div>
            <div className={'report1-page-toolbar'}>
                <div className={'kpi'}>
                    <div>{t('Total (VAT)')}</div>
                    <div>{kpis.vatTotal.toFixed(2)}</div>
                </div>
                <div className={'kpi'}>
                    <div>{t('Total (NET)')}</div>
                    <div>{kpis.netTotal.toFixed(2)}</div>
                </div>
                <div className={'kpi'}>
                    <div>{t('Sales Total (VAT)')}</div>
                    <div>{kpis.salesVatTotal.toFixed(2)}</div>
                </div>
                <div className={'kpi'}>
                    <div>{t('Sales Total (NET)')}</div>
                    <div>{kpis.salesNetTotal.toFixed(2)}</div>
                </div>
            </div>
            <br/>
            <div className="table-wrapper">
                <Table
                    data={report7IndexTable}
                    noPagination
                    dataManager={dataManager}
                    noMeta
                    customStyle
                    groupBy={['customer']}
                    sortBy={[{id: 'customer', desc: false}]}
                    expanded={report7IndexTable
                        .items
                        .reduce((p, n) => ({
                            ...p,
                            ['customer:' + n.customer]: true
                        }), {})
                    }
                    columns={[
                        {
                            Header: t('Sales Person'),
                            accessor: 'salesPersonName',
                            Filter: TextFilter,
                            filter: 'includes',
                        },
                        {
                            Header: t('customer'),
                            accessor: 'customer',
                            Filter: TextFilter,
                            filter: 'includes',
                        },
                        {
                            Header: t('Address Erp Code'),
                            accessor: 'addressErpCode',
                            Filter: TextFilter,
                            filter: 'includes',
                        },
                        {
                            Header: t('address'),
                            accessor: 'address',
                            Filter: TextFilter,
                            filter: 'includes',
                        },
                        {
                            Header: t('Country Code'),
                            accessor: 'countryCode',
                            Filter: TextFilter,
                            filter: 'includes',
                        },
                        {
                            Header: t('ciIdCode'),
                            accessor: 'ciIdCode',
                            Filter: TextFilter,
                            filter: 'includes',
                        },
                        {
                            Header: t('Total (VAT)'),
                            accessor: 'vatTotal',
                            aggregate: 'sum',
                            Filter: NumberRangeFilter,
                            filter: 'between',
                            Cell: NumberCell
                        },
                        {
                            Header: t('Total (NET)'),
                            accessor: 'netTotal',
                            aggregate: 'sum',
                            Filter: NumberRangeFilter,
                            filter: 'between',
                            Cell: NumberCell
                        },
                        {
                            Header: t('Sales Total (VAT)'),
                            accessor: 'salesVatTotal',
                            aggregate: 'sum',
                            Filter: NumberRangeFilter,
                            filter: 'between',
                            Cell: NumberCell
                        },
                        {
                            Header: t('Sales Total (NET)'),
                            accessor: 'salesNetTotal',
                            aggregate: 'sum',
                            Filter: NumberRangeFilter,
                            filter: 'between',
                            Cell: NumberCell
                        }
                    ]}
                />
            </div>
        </div>
    )
}

export default withPermissionHOC(Report7, [Permissions.AccessSalesPerson])
